// @flow
import React from 'react'
import Link from 'gatsby-link'
import styled, { css } from 'styled-components'
import GatsbyLink from 'gatsby-link'

import { lighten, darken } from 'polished'
import hello from 'hello-color'

import colors from '../theme/colors'
import * as _colors from '../theme/_colors'
import mapProps from '../theme/mapProps'
import {
  Headline,
  ContentRow,
  ViewportRow,
  ViewportContent,
  ViewportContainer,
  PageContainer,
  Container,
  Block,
  Fill,
  Flex,
  Title,
  Text,
  Paragraph,
} from '../styleguide'

type TierFlow = {
  id: string,
  title: string,
  price: number,
  color: string,
  CTA: string | void,
}

export default props => (
  <ContentRow>
    <Title h="2" scale="3" mx="auto" my="0" width="max">
      Plans and Pricing
    </Title>
    <Paragraph mx="auto" mb="3" width="max">
      Duis occaecat occaecat quis et ad aute ullamco sit sint.
    </Paragraph>
    <Flex align="center" justify="center">
      {TIERS.map((tier, i: number) => {
        return [i > 0 && <Fill key={i} flex="tenth" scale="2" />, <Tier key={tier.title} {...tier} />]
      })}
    </Flex>
  </ContentRow>
)

const TIERS = [
  {
    id: 'student',
    title: 'Student',
    price: 9,
    color: colors.cyan[4],
  },
  {
    id: 'pro',
    title: 'Pro',
    price: 99,
    color: colors.violet[6],
  },
]

const TierContainer = styled(Fill).attrs({ column: true, align: 'center', flex: 'fifth', p: 2 })`
  min-height: 20rem;
  background-color: ${colors.gray[6]};
  color: ${colors.white};
  border-radius: 0.5rem;
  ${mapProps({
    background: background => css`
      background-color: ${background};`,
  })};
`
const Price = styled(Title)`
  font-size: 4rem;
`

const TierNote = styled(Paragraph)`
  max-width: 12rem;
`

const Tier = ({ id, title, price, color, CTA }: TierFlow) => {
  return (
    <TierContainer background={color}>
      <Title h="3" pt="2" scale="2">
        {title}
      </Title>
      <Fill flex="fourth" scale="2" />
      <Flex>
        <Title weight="light" color={colors.gray[2]}>
          $
        </Title>
        <Price px="1">{price}</Price>
      </Flex>
      <Fill flex="fourth" scale="2" />
      <Paragraph lines="2">per user per month billed monthly</Paragraph>
      <GatsbyLink to={'/pay' + `?plan=${id}`}>
        <Button color={color}>{CTA || 'Get started'}</Button>
      </GatsbyLink>
    </TierContainer>
  )
}

const Form = styled.div`
  display: flex;
  align-items: center;
  ${'' /* margin-bottom: 2.5rem;
  padding-bottom: 2.5rem;
  border-bottom: .1875rem solid #e6ebf1; */} ${'' /* padding-top: 1rem; */} padding-bottom: 1.5rem;
  margin-bottom: 1.5rem;
  box-shadow: 0 .1875rem 0 #e6ebf1;
`
const Label = styled.label`
  color: #6b7c93;
  font-weight: 300;
  letter-spacing: 0.025em;
`
const Email = styled.input.attrs({ type: 'text', placeholder: 'tom@jerry.com' })`
  color: '#424770';
  font-size: 1.5rem;
  line-height: 2rem;
  letter-spacing: '0.025em';
  ::placeholder {
    color: #aab7c4;
  }

  display: block;
  margin: .625rem 0 1.25rem 0;
  max-width: 30rem;
  padding: .625rem .875rem;
  box-shadow: rgba(50, 50, 93, 0.14902) 0 .0625rem .1875rem, rgba(0, 0, 0, 0.0196078) 0 .0625rem 0;
  border-radius: .25rem;
  background: white;

  transition: all 150ms ease;
  &:hover {
    ${'' /* box-shadow:
      rgba(50, 50, 93, 0.109804) 0 .125rem .0625rem,
      rgba(0, 0, 0, 0.0784314) 0 0 .0625rem
    ; */};
    box-shadow: rgba(50, 50, 93, 0.109804) 0 .25rem .375rem, rgba(0, 0, 0, 0.0784314) 0 .0625rem .1875rem;
    transition: all 150ms ease;
  }
  &:focus {
    ${'' /* box-shadow:
      rgba(50, 50, 93, 0.109804) 0 .25rem .375rem,
      rgba(0, 0, 0, 0.0784314) 0 .0625rem .1875rem
    ; */};
    box-shadow: 0 .4375rem .875rem rgba(50, 50, 93, 0.1), 0 .1875rem .375rem rgba(0, 0, 0, 0.08);
    transition: all 150ms ease;
  }
`

const Button = styled.button`
  white-space: nowrap;
  border: 0;
  outline: 0;
  display: inline-block;
  height: 2.5rem;
  line-height: 2.5rem;
  padding: 0 .875rem;
  box-shadow: 0 .25rem .375rem rgba(50, 50, 93, 0.11), 0 .0625rem .1875rem rgba(0, 0, 0, 0.08);
  color: #fff;
  border-radius: .25rem;
  font-size: .9375rem;
  font-weight: 600;
  letter-spacing: 0.025em;
  background-color: ${colors.indigo[6]};
  text-decoration: none;
  transition: all 150ms ease;
  margin-top: 0.75rem;
  &:hover {
    color: #fff;
    cursor: pointer;
    background-color: #7795f8;
    transform: translateY(-.0625rem);
    box-shadow: 0 .4375rem .875rem rgba(50, 50, 93, 0.1), 0 .1875rem .375rem rgba(0, 0, 0, 0.08);
  }
  &:active {
    color: #fff;
    cursor: pointer;
    background-color: ${colors.indigo[8]};
    transform: translateY(.0625rem);
    box-shadow: 0 .1875rem .3125rem rgba(50, 50, 93, 0.1), 0 0 .0625rem rgba(0, 0, 0, 0.08);
  }

  ${mapProps({
    color: color => {
      const lighter = lighten(0.05, color)
      const darker = darken(0, color)
      return css`
        color: ${color};
        background-color: ${colors.white};
        &:hover {
          background-color: ${lighter};
        }
        &:active {
          background-color: ${darker};
        }`
    },
  })};
`



// WEBPACK FOOTER //
// ./src/pages/pricing.js